import React, { useState, useCallback } from 'react';
import { Upload, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

/* ── Constantes de mapping ───────────────────────────────────────────── */
const TARGET_FIELDS = [
  { id:'nom',       label:'Nom *',         required:true },
  { id:'email',     label:'Email',         required:false },
  { id:'telephone', label:'Téléphone',    required:false },
  { id:'group_id',  label:'Groupe',        required:false },
  { id:'diet',      label:'Régime',        required:false },
  { id:'plus_one',  label:'+1 Nom',        required:false },
  { id:'note',      label:'Note/Allergie', required:false },
];

const GUESS_PATTERNS = {
  nom:       /nom|pr[ée]nom|name|invit/i,
  email:     /email|mail|courriel/i,
  telephone: /t[ée]l|phone|mobile/i,
  group_id:  /groupe|group|table/i,
  diet:      /r[ée]gime|diet|allergi/i,
  plus_one:  /\+1|accompagnant|conjoint/i,
  note:      /note|remarque|commentaire/i,
};

/* ── Parse CSV/TSV ───────────────────────────────────────────────────────── */
function parseCSVText(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return null;
  const sep = lines[0].includes('\t') ? '\t'
            : lines[0].includes(';')  ? ';' : ',';
  const headers = lines[0].split(sep).map(h => h.trim().replace(/^["']+|["']+$/g,''));
  const rows    = lines.slice(1).map(line =>
    line.split(sep).map(c => c.trim().replace(/^["']+|["']+$/g,''))
  );
  return { headers, rows };
}

function guessMapping(headers) {
  const mapping = {};
  headers.forEach((h, i) => {
    for (const [field, pattern] of Object.entries(GUESS_PATTERNS)) {
      if (!mapping[field] && pattern.test(h)) {
        mapping[field] = String(i);
      }
    }
  });
  return mapping;
}

/* ── Composant principal ─────────────────────────────────────────────────── */
export default function ImportModal({ open, onClose, groups, onImport }) {
  const [parsed,       setParsed]      = useState(null);  // {headers, rows}
  const [mapping,      setMapping]     = useState({});
  const [defaultGroup, setDefaultGroup]= useState(groups?.[0]?.id ?? '');
  const [quickList,    setQuickList]   = useState('');
  const [pasteText,    setPasteText]   = useState('');
  const [dragOver,     setDragOver]    = useState(false);
  const [importing,    setImporting]   = useState(false);

  const handleTextChange = useCallback((text) => {
    setPasteText(text);
    if (text.trim().length < 10) { setParsed(null); return; }
    const result = parseCSVText(text);
    if (result) {
      setParsed(result);
      setMapping(guessMapping(result.headers));
    } else {
      setParsed(null);
    }
  }, []);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const result = parseCSVText(text);
      if (result) { setParsed(result); setMapping(guessMapping(result.headers)); }
      else toast.error('Fichier non lisible');
    };
    reader.readAsText(file, 'UTF-8');
  }, []);

  function buildInvites() {
    // Onglets 1 & 2 : parsed + mapping
    if (parsed) {
      if (!mapping.nom) return { error: 'Veuillez mapper la colonne Nom' };
      return {
        list: parsed.rows
          .filter(row => row[Number(mapping.nom)]?.trim())
          .map(row => ({
            nom:       row[Number(mapping.nom)].trim(),
            email:     mapping.email     ? (row[Number(mapping.email)]    || null) : null,
            telephone: mapping.telephone ? (row[Number(mapping.telephone)]|| '')   : '',
            group_id:  mapping.group_id  ? null : null, // groupe text → on ignore pour l'instant
            diet:      mapping.diet       ? (row[Number(mapping.diet)]?.toLowerCase().replace(/[ée]g[eé]/,'vegan').replace(/v[ée]g[eé]t/,'vegetarian') || 'standard') : 'standard',
            plus_one:  mapping.plus_one  && row[Number(mapping.plus_one)]?.trim()
              ? { name: row[Number(mapping.plus_one)].trim(), diet:'standard' } : null,
            note:      mapping.note      ? (row[Number(mapping.note)] || '') : '',
          }))
      };
    }
    // Onglet 3 : liste rapide
    if (quickList.trim()) {
      return {
        list: quickList.trim().split('\n')
          .map(l => l.trim()).filter(Boolean)
          .map(nom => ({ nom, group_id: defaultGroup || null }))
      };
    }
    return { error: 'Aucune donnée à importer' };
  }

  async function handleImport() {
    const { list, error } = buildInvites();
    if (error) { toast.error(error); return; }
    setImporting(true);
    try {
      const added = await onImport(list);
      toast.success(`${added} invité${added > 1 ? 's' : ''} importé${added > 1 ? 's' : ''} !`);
      onClose();
    } catch (e) {
      toast.error('Erreur import : ' + e.message);
    } finally {
      setImporting(false);
    }
  }

  const countToImport = parsed
    ? parsed.rows.filter(r => r[Number(mapping.nom)]?.trim()).length
    : quickList.trim().split('\n').filter(Boolean).length;

  function resetParsed() { setParsed(null); setPasteText(''); setQuickList(''); }

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { resetParsed(); onClose(); } }}>
      <DialogContent style={{ maxWidth: 680, maxHeight:'90vh', overflow:'auto', fontFamily:"'Montserrat',sans-serif" }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700 }}>
            Importer des invités
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="paste">
          <TabsList style={{ width:'100%', marginBottom:16 }}>
            <TabsTrigger value="paste"  style={{ flex:1, fontSize:12 }}>Coller depuis Excel</TabsTrigger>
            <TabsTrigger value="file"   style={{ flex:1, fontSize:12 }}>Fichier CSV</TabsTrigger>
            <TabsTrigger value="quick"  style={{ flex:1, fontSize:12 }}>Liste rapide</TabsTrigger>
          </TabsList>

          {/* Onglet 1 — Coller */}
          <TabsContent value="paste">
            <textarea
              value={pasteText}
              onChange={e => handleTextChange(e.target.value)}
              rows={8}
              placeholder={`Copiez vos lignes depuis Excel et collez ici.\nLa première ligne doit contenir les en-têtes.\n\nExemple :\nNom\tEmail\tGroupe\nSophie Aubert\tsophie@test.fr\tFamille`}
              style={{ ...taStyle, fontFamily:'monospace', fontSize:11 }}
            />
          </TabsContent>

          {/* Onglet 2 — Fichier CSV */}
          <TabsContent value="file">
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById('csv-file-input').click()}
              style={{
                border: `2px dashed ${dragOver ? '#D4AF37' : '#D1D5DB'}`,
                borderRadius:12, padding:'36px 20px', textAlign:'center',
                cursor:'pointer', background: dragOver ? 'rgba(212,175,55,.05)' : '#FAFAFA',
                transition:'border-color .2s',
              }}
            >
              <Upload size={28} style={{ color:'#9CA3AF', margin:'0 auto 8px' }} />
              <p style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Glissez un fichier CSV ici</p>
              <p style={{ fontSize:11, color:'#9CA3AF' }}>ou cliquez pour parcourir (.csv, .txt)</p>
              {parsed && <p style={{ fontSize:11, color:'#16A34A', marginTop:8 }}>✓ {parsed.rows.length} lignes détectées</p>}
            </div>
            <input id="csv-file-input" type="file" accept=".csv,.txt" style={{ display:'none' }}
              onChange={e => handleFile(e.target.files[0])} />
          </TabsContent>

          {/* Onglet 3 — Liste rapide */}
          <TabsContent value="quick">
            {groups.length > 0 && (
              <div style={{ marginBottom:10 }}>
                <label style={lblStyle}>Groupe par défaut</label>
                <Select value={defaultGroup} onValueChange={setDefaultGroup}>
                  <SelectTrigger style={{ fontSize:12, height:34 }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Aucun groupe</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>
                        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ width:10, height:10, borderRadius:'50%', background:g.color, display:'inline-block' }} />
                          {g.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <textarea
              value={quickList}
              onChange={e => setQuickList(e.target.value)}
              rows={10}
              placeholder={`Un nom par ligne :\n\nSophie Aubert\nThomas Bertin\nLucie Carrel\n...`}
              style={taStyle}
            />
          </TabsContent>
        </Tabs>

        {/* Étape 2 — Mapping colonnes (si parsed via onglets 1 ou 2) */}
        {parsed && (
          <div style={{ marginTop:16, padding:14, background:'#F9FAFB', borderRadius:10, border:'1px solid #E5E7EB' }}>
            <p style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:12 }}>
              Correspondance des colonnes
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
              {TARGET_FIELDS.map(f => (
                <div key={f.id}>
                  <label style={{ ...lblStyle, color: f.required && !mapping[f.id] ? '#EF4444' : '#374151' }}>
                    {f.label}
                  </label>
                  <Select value={mapping[f.id] ?? '__ignore__'}
                    onValueChange={v => setMapping(m => ({ ...m, [f.id]: v === '__ignore__' ? undefined : v }))}
                  >
                    <SelectTrigger style={{ fontSize:11, height:30 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ignore__">— Ignorer —</SelectItem>
                      {parsed.headers.map((h, i) => (
                        <SelectItem key={i} value={String(i)}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Aperçu 5 lignes */}
            <p style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', marginBottom:6 }}>Aperçu (5 premières lignes)</p>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
                <thead>
                  <tr style={{ background:'#F3F4F6' }}>
                    {TARGET_FIELDS.filter(f => mapping[f.id]).map(f => (
                      <th key={f.id} style={{ padding:'3px 6px', textAlign:'left', fontWeight:600, color:'#6B7280' }}>{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.slice(0,5).map((row, ri) => (
                    <tr key={ri} style={{ borderBottom:'1px solid #F3F4F6' }}>
                      {TARGET_FIELDS.filter(f => mapping[f.id]).map(f => (
                        <td key={f.id} style={{ padding:'3px 6px', color:'#374151' }}>
                          {row[Number(mapping[f.id])] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Barre de bas */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:16 }}>
          <span style={{ fontSize:12, color: countToImport > 0 ? '#16A34A' : '#9CA3AF', fontWeight:600 }}>
            {countToImport > 0 ? `${countToImport} invité${countToImport > 1 ? 's' : ''} détecté${countToImport > 1 ? 's' : ''}` : 'En attente de données…'}
          </span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => { resetParsed(); onClose(); }}
              style={{ ...btnBase, background:'#F3F4F6', color:'#374151' }}>Annuler</button>
            <button
              disabled={countToImport === 0 || importing}
              onClick={handleImport}
              style={{ ...btnBase, background:'#D4AF37', color:'#0B1E20', fontWeight:700,
                opacity: (countToImport === 0 || importing) ? 0.5 : 1, cursor: countToImport === 0 ? 'not-allowed' : 'pointer' }}>
              {importing ? 'Import…' : `Importer ${countToImport > 0 ? countToImport : ''} invité${countToImport > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const taStyle = { width:'100%', padding:'8px 10px', borderRadius:8, fontSize:12,
  border:'1px solid #D1D5DB', outline:'none', resize:'vertical', boxSizing:'border-box',
  fontFamily:"'Montserrat',sans-serif", lineHeight:1.6 };
const lblStyle = { display:'block', fontSize:11, fontWeight:600, color:'#374151', marginBottom:3 };
const btnBase  = { padding:'8px 18px', borderRadius:8, border:'none', fontSize:13,
  cursor:'pointer', fontFamily:"'Montserrat',sans-serif" };
